import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  generateBreadcrumbSchema,
  type BreadcrumbItem,
} from '@/lib/structured-data';

interface BreadcrumbsProps {
  /** Custom items. If omitted you must pass `items` yourself. */
  items: BreadcrumbItem[];
  /** Extra CSS classes on the <nav> wrapper. */
  className?: string;
}

/**
 * Accessible breadcrumb navigation with embedded JSON-LD BreadcrumbList.
 *
 * All items except the last one are clickable links.
 * Uses semantic <nav> with aria-label="Drobtine".
 */
export function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  const schema = generateBreadcrumbSchema(items);

  return (
    <>
      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <nav aria-label="Drobtine" className={className}>
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          {items.map((item, i) => {
            const isLast = i === items.length - 1;
            return (
              <li key={item.url} className="flex items-center gap-1.5">
                {i > 0 && (
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" aria-hidden="true" />
                )}
                {isLast ? (
                  <span className="text-foreground truncate max-w-[240px]" aria-current="page">
                    {item.name}
                  </span>
                ) : (
                  <Link
                    to={new URL(item.url).pathname + new URL(item.url).search}
                    className="hover:text-primary transition-colors whitespace-nowrap"
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
